import React, { useState, useCallback, useEffect, useRef } from 'react'
import { InputField, LinkButton, Modal } from '@enamelai/design-system'
import './AddInsuranceStyle.css'
import {
  checkPatientEligibility,
  addInsurancePlanByPatientId,
} from '@store/actions/insurance.action'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { PatientWithInsurance } from '@dataTypes/appointment.type'
import { searchPatientsByName } from '@services/api/patient.api'
import { getFullName } from '@utils/fullname.util'
import moment from 'moment'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'
import { getAllCarrierSettings } from '@services/api/carrier.api'

import { logger } from '@utils/safeLogger'

type AddInsuranceProps = {
  isVisible: boolean
  onSaveNewInsurancePlan: () => void
  closeModal: () => void
  currentPayers?: string[]
  familyMember?: {
    member_id: string
    first_name: string
    last_name: string
    date_of_birth: string
  }
}

type AddInsuranceFormData = {
  patientId: string
  patientDob: string
}

type PatientInput = {
  patientId: string | null
  value: string
}

const tempDataIndividual = (): AddInsuranceFormData => ({
  patientId: '',
  patientDob: '',
})

const AddInsurance: React.FC<AddInsuranceProps> = ({
  isVisible,
  onSaveNewInsurancePlan,
  closeModal,
  currentPayers,
  familyMember,
}: AddInsuranceProps): React.ReactElement => {
  const [formData, setFormData] =
    useState<AddInsuranceFormData>(tempDataIndividual())
  const dispatch = useAppDispatch()
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const { selectedPatient: selectedPatientState } = useAppSelector(
    (state) => state.patient,
  )
  const [selectedPatient, setSelectedPatient] = useState<PatientInput>({
    patientId: null,
    value: '',
  })
  const [approvedCarriers, setApprovedCarriers] = useState<string[]>([])
  const [debouncedPatientName, setDebouncedPatientName] = useState('')
  const [patients, setPatients] = useState<PatientWithInsurance[]>([])
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({})
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const patientNameInputRef = useRef<HTMLInputElement>(null)
  const memberIdInputRef = useRef<HTMLInputElement>(null)

  const fetchApprovedCarriers = useCallback(async (): Promise<void> => {
    try {
      const carrierSettingsResponse = await getAllCarrierSettings()
      if (
        carrierSettingsResponse.statusCode === 200 &&
        'carrier_settings' in carrierSettingsResponse.data
      ) {
        setApprovedCarriers(carrierSettingsResponse.data.carrier_settings)
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    fetchApprovedCarriers()
  }, [fetchApprovedCarriers])

  const fetchPatients = async (name: string): Promise<void> => {
    try {
      const response = await searchPatientsByName(name)
      if (response.statusCode === 200) {
        setPatients(response.data?.patients)
      } else {
        setPatients([])
      }
    } catch (error) {
      logger.error('Error fetching patients:', error)
      setPatients([])
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedPatientName.trim()) {
        fetchPatients(debouncedPatientName.trim())
      } else {
        setPatients([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [debouncedPatientName])

  const patientNameHandleChange = (value: string): void => {
    setSelectedPatient({ patientId: null, value })
    setDebouncedPatientName(value)
    setFormData((prevState) => ({
      ...prevState,
      patientId: '',
      patientDob: '',
    }))
    setFormErrors({
      ...formErrors,
    })
  }

  const handlePatientSelect = (patient: PatientWithInsurance): void => {
    const dob = moment.utc(patient.date_of_birth)
    const formattedDob = dob.format('MM/DD/YYYY')
    setSelectedPatient({
      patientId: patient.patient_id,
      value: getFullName(patient, true),
    })
    setPatients([])
    setFormData((prevState) => ({
      ...prevState,
      patientDob: formattedDob,
    }))
    setFormErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors }
      delete updatedErrors['patientDob']
      return updatedErrors
    })
  }

  const resetValues = (): void => {
    setFormData(tempDataIndividual())
    setFormErrors({})
    setSelectedPatient({
      patientId: null,
      value: '',
    })
    setPatients([])
  }

  const handleCancelClick = (): void => {
    resetValues()
    closeModal()
  }

  const validateRequiredFields = (
    fields: Partial<AddInsuranceFormData>,
    requiredFields: (keyof AddInsuranceFormData)[],
  ): { [key: string]: string } => {
    const errors: { [key: string]: string } = {}
    requiredFields.forEach((field) => {
      const value = fields[field]
      let formattedField = field
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase())
      if (!value) {
        if (field === 'patientId') formattedField = 'Member ID'
        errors[field] = `${formattedField} is required`
      }
    })
    return errors
  }

  const validateFields = (): boolean => {
    const requiredFields: (keyof AddInsuranceFormData)[] = [
      'patientId',
      'patientDob',
    ]

    const newErrors = validateRequiredFields(formData, requiredFields)
    setFormErrors(newErrors)

    return Object.keys(newErrors).length === 0
  }

  const handleSaveClick = async (): Promise<void> => {
    if (!validateFields()) {
      return
    }

    showLoading({
      container: 'body',
      hideElements: ['.insurance-container > .insurance-tab-content'],
    })

    // Remove this once we have production ready DentalXChange API
    // Mock service is used to simulate the third-party system while keeping our application aligned with their data structure.
    const dataForMockService = {
      ...formData,
      patientDetails: {
        memberId: formData.patientId,
        firstName: selectedPatientState?.first_name,
        lastName: selectedPatientState?.last_name,
        dateOfBirth: selectedPatientState?.date_of_birth.split('T')[0],
        address: {
          address1: selectedPatientState?.address,
          address2: null,
          city: selectedPatientState?.city,
          state: selectedPatientState?.state,
          zipCode: selectedPatientState?.zip,
        },
      },
      existingPatientPayers: currentPayers || [],
    }

    // await dispatch(checkPatientEligibility(formData))
    await dispatch(checkPatientEligibility(dataForMockService))
      .then(async (state) => {
        const payerIdCode =
          state?.payload?.response?.eligibilities[0]?.insurance?.payerIdCode
        if (
          payerIdCode &&
          approvedCarriers.length > 0 &&
          approvedCarriers
            .map((x: any) => {
              return x.carrier.payer_id
            })
            .includes(payerIdCode)
        ) {
          await dispatch(
            addInsurancePlanByPatientId({
              patientId: selectedPatient.patientId!,
              insuranceData: {
                ...state.payload.response,
                userId: user.user_id,
              },
            }),
          )

          onSaveNewInsurancePlan()
          resetValues()
          closeModal()
        } else {
          setTimeout(() => {
            resetValues()
            closeModal()
            hideLoading('body')
            if (payerIdCode) {
              showToast(
                createFailedToast(
                  "Insurance payer is not configured in the clinic's payer list",
                  "Insurance payer with payer ID code '" +
                    payerIdCode +
                    "' is not configured in the clinic's payer list.",
                ),
              )
            } else {
              showToast(
                createFailedToast(
                  'Insurance plan not found',
                  'Insurance plan not found. Please check the patient details and try again.',
                ),
              )
            }
          }, 2000)
        }
      })
      .catch((error) => {
        logger.error('Error occurred:', error)
        setTimeout(() => {
          hideLoading('body')
          showToast(
            createFailedToast(
              'Insurance plan not added',
              'Insurance plan not added. Please try again.',
            ),
          )
        }, 2000)
      })
  }

  const handleInputChange = useCallback(
    (field: string, value: string | number | boolean): void => {
      const processedValue =
        typeof value === 'string' && value.trim() === '' ? '' : value

      setFormData((prevState) => ({
        ...prevState,
        [field]: processedValue,
      }))

      setFormErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors }
        delete updatedErrors[field]
        return updatedErrors
      })
    },
    [],
  )

  useEffect(() => {
    if (familyMember && isVisible) {
      setSelectedPatient({
        patientId: familyMember.member_id,
        value: getFullName(familyMember, true),
      })
      setFormData((prevState) => ({
        ...prevState,
        patientDob: moment.utc(familyMember.date_of_birth).format('MM/DD/YYYY'),
      }))
    } else {
      if (selectedPatientState?.patient_id && isVisible) {
        setSelectedPatient({
          patientId: selectedPatientState?.patient_id,
          value: getFullName(selectedPatientState, true),
        })
        handlePatientSelect(
          selectedPatientState as unknown as PatientWithInsurance,
        )
      }
    }
  }, [familyMember, isVisible, selectedPatientState])

  useEffect(() => {
    if (!isVisible) return
    const timer = setTimeout(() => {
      const inputElement = selectedPatient.patientId
        ? memberIdInputRef.current
        : patientNameInputRef.current
      inputElement?.focus()
    }, 100)
    return () => clearTimeout(timer)
  }, [isVisible, selectedPatient.patientId])

  return (
    <Modal
      open={isVisible}
      onClose={handleCancelClick}
      size="md"
      title="Add insurance plan"
      closeOnBackdrop={false}
      secondaryLabel="Cancel"
      onSecondaryClick={handleCancelClick}
      primaryLabel="Save plan"
      onPrimaryClick={() => {
        void handleSaveClick()
      }}
    >
      <div className="add-insurance-form">
        <p className="add-insurance-section-label">Patient info</p>
        <div className="add-insurance-patient">
          <InputField
            ref={patientNameInputRef}
            variant="text"
            label="Name"
            placeholder="Search"
            value={selectedPatient.value || ''}
            onChange={(e) => patientNameHandleChange(e.target.value)}
            error={
              !!formErrors.patientFirstName || !!formErrors.patientLastName
            }
            helperText={
              formErrors.patientFirstName ||
              formErrors.patientLastName ||
              undefined
            }
            disabled={!!selectedPatient.patientId}
            clearable={false}
            fullWidth
          />
          {selectedPatient.patientId && !selectedPatientState && (
            <LinkButton
              size="sm"
              className="add-insurance-change-patient"
              onClick={() => patientNameHandleChange('')}
            >
              Change patient
            </LinkButton>
          )}
          {patients.length > 0 && (
            <ul className="add-insurance-patient-list" role="listbox">
              {patients.map((patient) => (
                <li
                  key={patient.patient_id}
                  role="option"
                  aria-selected={false}
                >
                  <button
                    type="button"
                    className="add-insurance-patient-item"
                    onClick={() => handlePatientSelect(patient)}
                  >
                    {patient.first_name} {patient.last_name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="add-insurance-row">
          <InputField
            ref={memberIdInputRef}
            variant="text"
            label="Member ID"
            value={formData.patientId}
            onChange={(e) => handleInputChange('patientId', e.target.value)}
            error={!!formErrors.patientId}
            helperText={formErrors.patientId || undefined}
            fullWidth
          />
          <InputField
            variant="text"
            label="Date of birth"
            value={formData.patientDob}
            onChange={(e) => handleInputChange('patientDob', e.target.value)}
            disabled
            error={!!formErrors.patientDob}
            helperText={formErrors.patientDob || undefined}
            fullWidth
          />
        </div>
      </div>
    </Modal>
  )
}
export default AddInsurance
